export type PreflightPlatformCode =
  | "META"
  | "YOUTUBE"
  | "SNAPCHAT"
  | "TIKTOK"
  | "DV360"
  | "ADFORM";

export type PreflightPlacementCode =
  | "FEED"
  | "STORIES"
  | "REELS"
  | "IN_STREAM"
  | "SHORTS"
  | "BUMPER"
  | "IN_FEED"
  | "PROGRAMMATIC";

/** Valid platform ↔ placement combinations (FE dropdown + create-report validation). */
export const PLATFORM_PLACEMENTS: Record<
  PreflightPlatformCode,
  readonly PreflightPlacementCode[]
> = {
  META: ["FEED", "STORIES", "REELS"],
  YOUTUBE: ["IN_STREAM", "SHORTS", "BUMPER"],
  SNAPCHAT: ["IN_FEED"],
  TIKTOK: ["IN_FEED"],
  // DSPs buy across surfaces; their guidelines vary by asset type, not by placement.
  DV360: ["PROGRAMMATIC"],
  ADFORM: ["PROGRAMMATIC"],
} as const;

function asNonEmptyReadonlyTuple<T extends string>(
  values: readonly T[],
): readonly [T, ...T[]] {
  if (values.length === 0) {
    throw new Error('Expected a non-empty list of string literals');
  }
  return values as readonly [T, ...T[]];
}

/** Platform codes in `PLATFORM_PLACEMENTS` definition order (for Zod/OpenAPI enums). */
export const PREFLIGHT_PLATFORMS = asNonEmptyReadonlyTuple(
  Object.keys(PLATFORM_PLACEMENTS) as PreflightPlatformCode[],
);

/** Placement codes used by at least one platform (stable order). */
export const PREFLIGHT_PLACEMENTS = asNonEmptyReadonlyTuple([
  ...new Set(PREFLIGHT_PLATFORMS.flatMap((platform) => PLATFORM_PLACEMENTS[platform])),
]);

export function isValidPlatformPlacement(
  platform: PreflightPlatformCode,
  placement: PreflightPlacementCode,
): boolean {
  return (PLATFORM_PLACEMENTS[platform] as readonly string[]).includes(placement);
}

export type PreflightAssetType = "IMAGE" | "VIDEO";

export interface PreflightCriterionDefinition {
  platform: PreflightPlatformCode;
  code: string;
  name: string;
  definition: string;
  /** When set, overrides `definition` for the matching asset type (AD-2044). */
  definitionByAssetType?: Partial<Record<PreflightAssetType, string>>;
  displayOrder: number;
  /** v1 default weight (0–100). Zero means inactive in the default weight set. */
  defaultWeight: number;
}

/** Catalogue definition for a scored asset; falls back to `definition` when no override exists. */
export function criterionDefinitionForAssetType(
  criterion: Pick<PreflightCriterionDefinition, "definition" | "definitionByAssetType"> | null | undefined,
  assetType: string,
): string | null {
  if (!criterion) return null;
  if (assetType === "IMAGE" || assetType === "VIDEO") {
    return criterion.definitionByAssetType?.[assetType] ?? criterion.definition;
  }
  return criterion.definition;
}

/** Whether a criterion participates in the v1 default weight set. */
export function isDefaultActiveCriterion(
  criterion: Pick<PreflightCriterionDefinition, "defaultWeight">,
): boolean {
  return criterion.defaultWeight > 0;
}

// Meta feature set (Publicis V2). 8 scored @ 12.5% each; the rest are report-only (0 weight) or
// always N/A. Names match the DS analyzer's feature labels; codes stay stable across renames.
const META_CRITERIA: readonly Omit<PreflightCriterionDefinition, "platform">[] = [
  {
    code: "branding",
    name: "Integrate Brand Early",
    definition:
      "Creative meets this criterion if it includes brand name, brand logo, or branded packaging.",
    definitionByAssetType: {
      IMAGE:
        "Creative meets this criterion if it includes brand name, brand logo, or branded packaging.",
      VIDEO:
        "Creative meets this criterion if it includes brand name, brand logo, or branded packaging within the first 2.99 seconds.",
    },
    displayOrder: 1,
    defaultWeight: 12.5,
  },
  {
    code: "simple-message",
    name: "Focus on 1 Main Message",
    definition:
      "Creative meets this criterion if no more than 10 overlaid headline words appear on any single frame. Brand logos, subtitles, packaging text, fine print, calls-to-action, and pricing/numbers are excluded from the count.",
    displayOrder: 2,
    defaultWeight: 12.5,
  },
  {
    code: "aspect-ratio",
    name: "Frame for Mobile - Aspect Ratio",
    definition:
      "Creative meets this criterion if its aspect ratio matches the accepted ratios for the selected channel and placement.",
    displayOrder: 3,
    defaultWeight: 12.5,
  },
  {
    code: "pacing",
    name: "Frame for Mobile - Pacing",
    definition:
      "Creative meets this criterion if there is a shot change detected within the first 5 seconds. These include hard cuts, soft transitions and camera changes such as camera pans, swipes, zooms, depth of field changes, tracking shots and movement of the camera.",
    displayOrder: 4,
    defaultWeight: 12.5,
  },
  {
    code: "optimal-length",
    name: "Optimal Length",
    definition:
      "Creative meets this criterion if the video duration is within the placement limit (up to 15 seconds for most Meta placements).",
    displayOrder: 5,
    defaultWeight: 12.5,
  },
  {
    code: "optimised-for-sound-off",
    name: "Optimised for Sound Off",
    definition:
      "Creative meets this criterion if spoken words are supported by on-screen subtitles or supers, detected via at least 40% word overlap between the audio transcript and on-screen text. Videos with no speech automatically pass.",
    displayOrder: 6,
    defaultWeight: 12.5,
  },
  {
    code: "safe-zones",
    name: "Safe Zones",
    definition:
      "Creative meets this criterion if no overlaid text falls inside the placement's safe-zone margins. Packaging text, scene text, and fine print are ignored — only digitally overlaid text, brand names, and logo text count.",
    displayOrder: 7,
    defaultWeight: 12.5,
  },
  {
    code: "thumbnail-branding",
    name: "Thumbnail Branding",
    definition:
      "Creative meets this criterion if branding — the brand name as text, or a recognizable logo/wordmark — is present in the first frame.",
    displayOrder: 8,
    defaultWeight: 12.5,
  },
  {
    code: "cta-native-mechanic",
    name: "CTA: Platform Button - CONVERSION",
    definition:
      "Always reported as not applicable — the conversion call-to-action is delivered by the platform's native button and cannot be detected from the creative itself.",
    displayOrder: 9,
    defaultWeight: 0,
  },
  {
    code: "music",
    name: "Music",
    definition:
      "Report-only. Detected if a melodic or rhythmic music track is present; sound effects, percussion-only stings, UI sounds, and silence do not count as music.",
    displayOrder: 10,
    defaultWeight: 0,
  },
  {
    code: "pacing-5s",
    name: "Frame for Mobile - Pacing 5s",
    definition:
      "Report-only. Creative is flagged if there are more than 4 visual shots in the first 5 seconds (too chaotic).",
    displayOrder: 11,
    defaultWeight: 0,
  },
  {
    code: "humanize",
    name: "Humanize + Branding",
    definition:
      "Report-only. Detected if a single face (human, pet, or animated), a single product, a logo, or the brand name fills at least 25% of the frame within the first 5 seconds. Full or partial body shots and side-on profiles with a visible face count.",
    displayOrder: 12,
    defaultWeight: 0,
  },
  {
    code: "integrate-brand-last-5s",
    name: "Integrate Brand Often - Last 5 Seconds",
    definition:
      "Report-only. Detected if the brand or product is seen in the last 5 seconds.",
    displayOrder: 13,
    defaultWeight: 0,
  },
  {
    code: "cta-platform-button",
    name: "CTA Platform Button",
    definition:
      "Always reported as not applicable — this is the platform's native (non-conversion) call-to-action button, which cannot be detected from the creative itself.",
    displayOrder: 14,
    defaultWeight: 0,
  },
  {
    code: "sound-on",
    name: "Sound On",
    definition: "Report-only. Detected if the video includes any audible sound.",
    displayOrder: 15,
    defaultWeight: 0,
  },
  {
    code: "text-overlay",
    name: "Text Overlay",
    definition:
      "Report-only. Detected if any digitally added text overlay or super has been incorporated into the video.",
    displayOrder: 16,
    defaultWeight: 0,
  },
] as const;

// YouTube feature set (Publicis V2). 9 scored @ 11.11% each; the rest are report-only (0 weight).
const YOUTUBE_CRITERIA: readonly Omit<PreflightCriterionDefinition, "platform">[] = [
  {
    code: "branding",
    name: "Integrate Brand Early",
    definition:
      "Creative meets this criterion if the brand — its name as text, or a recognizable logo/wordmark — appears within the first 5 seconds.",
    displayOrder: 1,
    defaultWeight: 11.11,
  },
  {
    code: "integrate-brand-last-5s",
    name: "Integrate Brand Often - Last 5 Seconds",
    definition:
      "Creative meets this criterion if the brand or product is seen in the last 5 seconds.",
    displayOrder: 2,
    defaultWeight: 11.11,
  },
  {
    code: "tight-framing",
    name: "Humanize + Branding",
    definition:
      "Creative meets this criterion if a single face (human, pet, or animated), a single product, a logo, or the brand name fills at least 25% of the frame within the first 5 seconds. Full or partial body shots and side-on profiles with a visible face count.",
    displayOrder: 3,
    defaultWeight: 11.11,
  },
  {
    code: "aspect-ratio",
    name: "Frame for Mobile - Aspect Ratio",
    definition:
      "Creative meets this criterion if its aspect ratio is 16:9, 9:16, 4:3, or 1:1.",
    displayOrder: 4,
    defaultWeight: 11.11,
  },
  {
    code: "brand-audio-mention",
    name: "Optimised for Sound - Brand Mention",
    definition:
      "Creative meets this criterion if the brand name is spoken or sung aloud in the audio, in any language or accent. The brand name may be the master brand or a sub-brand.",
    displayOrder: 5,
    defaultWeight: 11.11,
  },
  {
    code: "sound-on",
    name: "Optimised for Sound - Sound On",
    definition: "Creative meets this criterion if the video includes any audible sound.",
    displayOrder: 6,
    defaultWeight: 11.11,
  },
  {
    code: "cta-in-creative",
    name: "CTA: In Creative",
    definition:
      "Creative meets this criterion if an on-screen call-to-action phrase (e.g. Shop Now, Learn More) is detected at any time within the video.",
    displayOrder: 7,
    defaultWeight: 11.11,
  },
  {
    code: "pacing",
    name: "Frame for Mobile - Pacing",
    definition:
      "Creative meets this criterion if there is a shot change detected within the first 5 seconds. These include hard cuts, soft transitions and camera changes such as camera pans, swipes, zooms, depth of field changes, tracking shots and movement of the camera.",
    displayOrder: 8,
    defaultWeight: 11.11,
  },
  {
    code: "safe-zones",
    name: "Safe Zone Rule",
    definition:
      "Creative meets this criterion if no overlaid text falls inside YouTube's safe-zone margins. Packaging text, scene text, and fine print are ignored — only digitally overlaid text, brand names, and logo text count.",
    displayOrder: 9,
    defaultWeight: 11.11,
  },
  {
    code: "focus-1-message",
    name: "Focus on 1 Main Message",
    definition:
      "Report-only. Detected if no more than 10 overlaid headline words appear on any single frame. Brand logos, subtitles, packaging text, fine print, calls-to-action, and pricing/numbers are excluded from the count.",
    displayOrder: 10,
    defaultWeight: 0,
  },
  {
    code: "pacing-5s",
    name: "Frame for Mobile - Pacing 5s",
    definition:
      "Report-only. Creative is flagged if there are more than 4 visual shots in the first 5 seconds (too chaotic).",
    displayOrder: 11,
    defaultWeight: 0,
  },
  {
    code: "optimal-length",
    name: "Optimal Length",
    definition:
      "Report-only. Creative is flagged if the video exceeds 30 seconds.",
    displayOrder: 12,
    defaultWeight: 0,
  },
] as const;

// Snapchat feature set (Publicis V2). 8 scored @ 12.5% each; Pacing 5s and Integrate Brand Often
// are report-only.
const SNAPCHAT_CRITERIA: readonly Omit<PreflightCriterionDefinition, "platform">[] = [
  {
    code: "simple-message",
    name: "Focus on 1 Main Message",
    definition:
      "Creative meets this criterion if no more than 10 overlaid headline words appear on any single frame. Brand logos, subtitles, packaging text, fine print, calls-to-action, and pricing/numbers are excluded from the count.",
    displayOrder: 1,
    defaultWeight: 12.5,
  },
  {
    code: "aspect-ratio",
    name: "Frame for Mobile - Aspect Ratio",
    definition: "Creative meets this criterion if its aspect ratio is 9:16.",
    definitionByAssetType: {
      IMAGE: "Creative meets this criterion if the image aspect ratio is 9:16.",
      VIDEO: "Creative meets this criterion if the video aspect ratio is 9:16.",
    },
    displayOrder: 2,
    defaultWeight: 12.5,
  },
  {
    code: "pacing",
    name: "Frame for Mobile - Pacing",
    definition:
      "Creative meets this criterion if there is a shot change detected within the first 5 seconds. These include hard cuts, soft transitions and camera changes such as camera pans, swipes, zooms, depth of field changes, tracking shots and movement of the camera.",
    displayOrder: 3,
    defaultWeight: 12.5,
  },
  {
    code: "pacing-5s",
    name: "Frame for Mobile - Pacing 5s",
    definition:
      "Report-only. Creative is flagged if there are more than 4 visual shots in the first 5 seconds (too chaotic).",
    displayOrder: 4,
    defaultWeight: 0,
  },
  {
    code: "humanize",
    name: "Humanize + Branding",
    definition:
      "Creative meets this criterion if a single face (human, pet, or animated), a single product, a logo, or the brand name fills at least 25% of the frame within the first 5 seconds. Full or partial body shots and side-on profiles with a visible face count.",
    displayOrder: 5,
    defaultWeight: 12.5,
  },
  {
    code: "integrate-brand-last-5s",
    name: "Integrate Brand Often - Last 5 Seconds",
    definition:
      "Report-only. Detected if the brand or product is seen in the last 5 seconds.",
    displayOrder: 6,
    defaultWeight: 0,
  },
  {
    code: "branding",
    name: "Integrate Brand Early",
    definition:
      "Creative meets this criterion if it includes brand name, brand logo, or branded packaging.",
    definitionByAssetType: {
      IMAGE:
        "Creative meets this criterion if it includes brand name, brand logo, or branded packaging.",
      VIDEO:
        "Creative meets this criterion if it includes brand name, brand logo, or branded packaging within the first 2.99 seconds.",
    },
    displayOrder: 7,
    defaultWeight: 12.5,
  },
  {
    code: "optimal-length",
    name: "Optimal Length",
    definition:
      "Creative meets this criterion if the video is no more than 15 seconds in length.",
    displayOrder: 8,
    defaultWeight: 12.5,
  },
  {
    code: "sound-on",
    name: "Optimised for Sound - Sound On",
    definition: "Creative meets this criterion if the video includes any audible sound.",
    displayOrder: 9,
    defaultWeight: 12.5,
  },
  {
    code: "safe-zones",
    name: "Safe Zones",
    definition:
      "Creative meets this criterion if no overlaid text falls inside Snapchat's safe-zone margins. Packaging text, scene text, and fine print are ignored — only digitally overlaid text, brand names, and logo text count.",
    displayOrder: 10,
    defaultWeight: 12.5,
  },
] as const;

// TikTok feature set (Publicis V2). Video-only, 6 scored @ 16.67% each, no report-only criteria.
const TIKTOK_CRITERIA: readonly Omit<PreflightCriterionDefinition, "platform">[] = [
  {
    code: "simple-message",
    name: "Focus on 1 Main Message",
    definition:
      "Creative meets this criterion if no more than 10 overlaid headline words appear on any single frame. Brand logos, subtitles, packaging text, fine print, calls-to-action, and pricing/numbers are excluded from the count.",
    displayOrder: 1,
    defaultWeight: 16.67,
  },
  {
    code: "aspect-ratio",
    name: "Frame for Mobile - Aspect Ratio",
    definition: "Creative meets this criterion if the video aspect ratio is 9:16.",
    displayOrder: 2,
    defaultWeight: 16.67,
  },
  {
    code: "tight-framing",
    name: "Frame for Mobile - Tight Framing",
    definition:
      "Creative meets this criterion if a single face (human, pet, or animated), a single product, a logo, or the brand name fills at least 25% of the frame within the first 5 seconds. Full or partial body shots and side-on profiles with a visible face count.",
    displayOrder: 3,
    defaultWeight: 16.67,
  },
  {
    code: "branding",
    name: "Integrate Brand Early",
    definition:
      "Creative meets this criterion if it includes brand name, brand logo, or branded packaging within the first 3 seconds.",
    displayOrder: 4,
    defaultWeight: 16.67,
  },
  {
    code: "sound-on",
    name: "Optimised for Sound - Sound On",
    definition:
      "Creative meets this criterion if the video includes any audible sound, such as speech or music.",
    displayOrder: 5,
    defaultWeight: 16.67,
  },
  {
    code: "break-4th-wall",
    name: "Break the 4th Wall",
    definition:
      "Creative meets this criterion if the viewer is addressed directly — either by a person on screen making direct contact, or through superimposed text posing a question or statement. Talent looking down the camera or asking questions to provoke a response both count.",
    displayOrder: 6,
    defaultWeight: 16.67,
  },
] as const;

// DV360 aspect ratio lists are shared with Adform (both are programmatic DSPs using the same
// accepted-ratio table).
const DSP_ASPECT_RATIO_BY_ASSET_TYPE: Partial<Record<PreflightAssetType, string>> = {
  IMAGE:
    "Creative meets this criterion if the image aspect ratio is one of 1:1, 1:2, 1:5, 2:3, 2:7, 3:1, 4:15, 6:1, 6:5, 9:16, 16:5, 32:5, 39:5, 45:74, 97:9, 97:25, or 364:45.",
  VIDEO:
    "Creative meets this criterion if the video aspect ratio is one of 1:1, 3:4, 4:3, or 9:16.",
};

// DV360 feature set (Publicis V2). 10 scored @ 10% each; Optimal Length is report-only.
const DV360_CRITERIA: readonly Omit<PreflightCriterionDefinition, "platform">[] = [
  {
    code: "simple-message",
    name: "Focus on 1 Main Message",
    definition:
      "Creative meets this criterion if no more than 10 overlaid headline words appear on any single frame. Brand logos, subtitles, packaging text, fine print, calls-to-action, and pricing/numbers are excluded from the count.",
    displayOrder: 1,
    defaultWeight: 10,
  },
  {
    code: "aspect-ratio",
    name: "Aspect Ratio",
    definition:
      "Creative meets this criterion if its aspect ratio is one of DV360's accepted ratios for the asset type.",
    definitionByAssetType: DSP_ASPECT_RATIO_BY_ASSET_TYPE,
    displayOrder: 2,
    defaultWeight: 10,
  },
  {
    code: "pacing",
    name: "Frame for Mobile - Pacing",
    definition:
      "Creative meets this criterion if there is a shot change detected within the first 5 seconds. These include hard cuts, soft transitions and camera changes such as camera pans, swipes, zooms, depth of field changes, tracking shots and movement of the camera.",
    displayOrder: 3,
    defaultWeight: 10,
  },
  {
    code: "text-overlay",
    name: "Text Overlay",
    definition:
      "Creative meets this criterion if any supers (text overlays) have been incorporated into the video. Subtitles, superimposed logos, in-situ text, packaging text, and T&Cs do not count.",
    displayOrder: 4,
    defaultWeight: 10,
  },
  {
    code: "humanize",
    name: "Humanize + Branding",
    definition:
      "Creative meets this criterion if a close up of a single face (human, pet, or animated), a single product, a logo, or the brand name fills at least 25% of the frame within the first 5 seconds. Full or partial body shots and side-on profiles with a visible face count.",
    displayOrder: 5,
    defaultWeight: 10,
  },
  {
    code: "branding",
    name: "Integrate Brand Early",
    definition:
      "Creative meets this criterion if it includes brand name or brand logo early in the video.",
    displayOrder: 6,
    defaultWeight: 10,
  },
  {
    code: "brand-audio-mention",
    name: "Optimised for Sound - Brand Mention",
    definition:
      "Creative meets this criterion if the brand name is mentioned in the audio output. The brand name may be the master brand or a sub-brand. Subtitles and superimposed text do not count.",
    displayOrder: 7,
    defaultWeight: 10,
  },
  {
    code: "sound-on",
    name: "Optimised for Sound - Sound On",
    definition: "Creative meets this criterion if the video includes any audible sound.",
    displayOrder: 8,
    defaultWeight: 10,
  },
  {
    code: "cta-in-creative",
    name: "CTA: In Creative",
    definition:
      "Creative meets this criterion if an on-screen call-to-action word or phrase is detected in the video.",
    displayOrder: 9,
    defaultWeight: 10,
  },
  {
    code: "safe-zones",
    name: "Safe Zone Rule",
    definition:
      "Creative meets this criterion if no overlaid text falls inside DV360's safe-zone margins. Packaging text, scene text, and fine print are ignored — only digitally overlaid text, brand names, and logo text count.",
    displayOrder: 10,
    defaultWeight: 10,
  },
  {
    code: "optimal-length",
    name: "Optimal Length",
    definition: "Report-only. Creative is flagged if the video exceeds 30 seconds.",
    displayOrder: 11,
    defaultWeight: 0,
  },
] as const;

// Adform feature set (Publicis V2). 8 scored @ 12.5% each; Motion and Optimal Length are
// report-only. Not a copy of DV360 — Adform adds Motion and omits CTA and safe zones.
const ADFORM_CRITERIA: readonly Omit<PreflightCriterionDefinition, "platform">[] = [
  {
    code: "simple-message",
    name: "Focus on 1 Main Message",
    definition:
      "Creative meets this criterion if no more than 10 overlaid headline words appear on any single frame. Brand logos, subtitles, packaging text, fine print, calls-to-action, and pricing/numbers are excluded from the count.",
    displayOrder: 1,
    defaultWeight: 12.5,
  },
  {
    code: "aspect-ratio",
    name: "Aspect Ratio",
    definition:
      "Creative meets this criterion if its aspect ratio is one of Adform's accepted ratios for the asset type.",
    definitionByAssetType: DSP_ASPECT_RATIO_BY_ASSET_TYPE,
    displayOrder: 2,
    defaultWeight: 12.5,
  },
  {
    code: "pacing",
    name: "Frame for Mobile - Pacing",
    definition:
      "Creative meets this criterion if there is a shot change detected within the first 5 seconds. These include hard cuts, soft transitions and camera changes such as camera pans, swipes, zooms, depth of field changes, tracking shots and movement of the camera.",
    displayOrder: 3,
    defaultWeight: 12.5,
  },
  {
    code: "text-overlay",
    name: "Text Overlay",
    definition:
      "Creative meets this criterion if any supers (text overlays) have been incorporated into the video. Subtitles, superimposed logos, in-situ text, packaging text, and T&Cs do not count.",
    displayOrder: 4,
    defaultWeight: 12.5,
  },
  {
    code: "humanize",
    name: "Humanize + Branding",
    definition:
      "Creative meets this criterion if a close up of a single face (human, pet, or animated), a single product, a logo, or the brand name fills at least 25% of the frame within the first 5 seconds. Full or partial body shots and side-on profiles with a visible face count.",
    displayOrder: 5,
    defaultWeight: 12.5,
  },
  {
    code: "branding",
    name: "Integrate Brand Early",
    definition:
      "Creative meets this criterion if it includes brand name or brand logo early in the video.",
    displayOrder: 6,
    defaultWeight: 12.5,
  },
  {
    code: "brand-audio-mention",
    name: "Optimised for Sound - Brand Mention",
    definition:
      "Creative meets this criterion if the brand name is mentioned in the audio output. The brand name may be the master brand or a sub-brand. Subtitles and superimposed text do not count.",
    displayOrder: 7,
    defaultWeight: 12.5,
  },
  {
    code: "motion",
    name: "Motion",
    definition: "Report-only. Detected if there is any motion in the first second.",
    displayOrder: 8,
    defaultWeight: 0,
  },
  {
    code: "sound-on",
    name: "Optimised for Sound - Sound On",
    definition: "Creative meets this criterion if the video includes any audible sound.",
    displayOrder: 9,
    defaultWeight: 12.5,
  },
  {
    code: "optimal-length",
    name: "Optimal Length",
    definition: "Report-only. Creative is flagged if the video exceeds 30 seconds.",
    displayOrder: 10,
    defaultWeight: 0,
  },
] as const;

export const PREFLIGHT_CRITERIA: readonly PreflightCriterionDefinition[] = [
  ...META_CRITERIA.map((c) => ({ ...c, platform: "META" as const })),
  ...YOUTUBE_CRITERIA.map((c) => ({ ...c, platform: "YOUTUBE" as const })),
  ...SNAPCHAT_CRITERIA.map((c) => ({ ...c, platform: "SNAPCHAT" as const })),
  ...TIKTOK_CRITERIA.map((c) => ({ ...c, platform: "TIKTOK" as const })),
  ...DV360_CRITERIA.map((c) => ({ ...c, platform: "DV360" as const })),
  ...ADFORM_CRITERIA.map((c) => ({ ...c, platform: "ADFORM" as const })),
];

/** DS feature `name` → stable catalogue `code` when labels drift from display names. */
// Explicit DS feature name → stable code map. Covers both the current DS labels (Publicis V2) and
// legacy labels emitted by older analyzer versions, so historical reports still resolve. Checked
// before the exact-name fallback in criterionForDsFeature.
export const DS_FEATURE_ALIAS: Record<PreflightPlatformCode, Readonly<Record<string, string>>> = {
  META: {
    // current
    "Integrate Brand Early": "branding",
    "Focus on 1 Main Message": "simple-message",
    "Frame for Mobile - Aspect Ratio": "aspect-ratio",
    "Frame for Mobile - Pacing": "pacing",
    "Frame for Mobile - Pacing 5s": "pacing-5s",
    "Optimised for Sound Off": "optimised-for-sound-off",
    "Safe Zones": "safe-zones",
    "Thumbnail Branding": "thumbnail-branding",
    "CTA: Platform Button - CONVERSION": "cta-native-mechanic",
    "CTA Platform Button": "cta-platform-button",
    "Humanize + Branding": "humanize",
    "Integrate Brand Often - Last 5 Seconds": "integrate-brand-last-5s",
    // legacy / spelling variants
    "Optimized for Sound Off": "optimised-for-sound-off",
    "Humanize + branding": "humanize",
    "Simple Message": "simple-message",
    "Aspect Ratio": "aspect-ratio",
    "Branding": "branding",
    "CTA: Native Mechanic": "cta-native-mechanic",
    "Call to Action: Native Mechanic": "cta-native-mechanic",
  },
  YOUTUBE: {
    // current
    "Integrate Brand Early": "branding",
    "Integrate Brand Often - Last 5 Seconds": "integrate-brand-last-5s",
    "Humanize + Branding": "tight-framing",
    "Frame for Mobile - Aspect Ratio": "aspect-ratio",
    "Optimised for Sound - Brand Mention": "brand-audio-mention",
    "Optimised for Sound - Sound On": "sound-on",
    "CTA: In Creative": "cta-in-creative",
    "Frame for Mobile - Pacing": "pacing",
    "Safe Zone Rule": "safe-zones",
    "Focus on 1 Main Message": "focus-1-message",
    "Frame for Mobile - Pacing 5s": "pacing-5s",
    // legacy / spelling variants
    "Humanize + branding": "tight-framing",
    "Tight Framing": "tight-framing",
    "Brand Audio Mention": "brand-audio-mention",
    "Sound On": "sound-on",
    "Aspect Ratio": "aspect-ratio",
    "Safe Zones": "safe-zones",
    "Pacing": "pacing",
    "Branding": "branding",
    "Call to Action: In Creative": "cta-in-creative",
  },
  SNAPCHAT: {
    // current
    "Focus on 1 Main Message": "simple-message",
    "Frame for Mobile - Aspect Ratio": "aspect-ratio",
    "Frame for Mobile - Pacing": "pacing",
    "Frame for Mobile - Pacing 5s": "pacing-5s",
    "Humanize + Branding": "humanize",
    "Integrate Brand Often - Last 5 Seconds": "integrate-brand-last-5s",
    "Integrate Brand Early": "branding",
    "Optimal Length": "optimal-length",
    "Optimised for Sound - Sound On": "sound-on",
    "Safe Zones": "safe-zones",
    // spelling / casing variants
    "Humanize + branding": "humanize",
    '"Humanize" + Branding': "humanize",
    "Optimized for Sound - Sound On": "sound-on",
    "Sound On": "sound-on",
    "Aspect Ratio": "aspect-ratio",
    "Branding": "branding",
    "Simple Message": "simple-message",
  },
  TIKTOK: {
    // current
    "Focus on 1 Main Message": "simple-message",
    "Frame for Mobile - Aspect Ratio": "aspect-ratio",
    "Frame for Mobile - Tight Framing": "tight-framing",
    "Integrate Brand Early": "branding",
    "Optimised for Sound - Sound On": "sound-on",
    "Break the 4th Wall": "break-4th-wall",
    // spelling / casing variants
    "Optimized for Sound - Sound On": "sound-on",
    "Sound On": "sound-on",
    "Aspect Ratio": "aspect-ratio",
    "Tight Framing": "tight-framing",
    "Branding": "branding",
    "Simple Message": "simple-message",
    "Break the 4th wall": "break-4th-wall",
    "Break the Fourth Wall": "break-4th-wall",
  },
  DV360: {
    // current
    "Focus on 1 Main Message": "simple-message",
    "Aspect Ratio": "aspect-ratio",
    "Frame for Mobile - Pacing": "pacing",
    "Text Overlay": "text-overlay",
    "Humanize + Branding": "humanize",
    "Integrate Brand Early": "branding",
    "Optimised for Sound - Brand Mention": "brand-audio-mention",
    "Optimised for Sound - Sound On": "sound-on",
    "CTA: In Creative": "cta-in-creative",
    "Safe Zone Rule": "safe-zones",
    "Optimal Length": "optimal-length",
    // spelling / casing variants — the guideline workbook quotes "Humanize" and lowercases
    // "safe zone rule", so both spellings resolve.
    '"Humanize" + Branding': "humanize",
    "Humanize + branding": "humanize",
    "safe zone rule": "safe-zones",
    "Safe Zones": "safe-zones",
    "Optimized for Sound - Brand Mention": "brand-audio-mention",
    "Optimized for Sound - Sound On": "sound-on",
    "Brand Audio Mention": "brand-audio-mention",
    "Sound On": "sound-on",
    "Branding": "branding",
    "Simple Message": "simple-message",
    "Call to Action: In Creative": "cta-in-creative",
  },
  ADFORM: {
    // current
    "Focus on 1 Main Message": "simple-message",
    "Aspect Ratio": "aspect-ratio",
    "Frame for Mobile - Pacing": "pacing",
    "Text Overlay": "text-overlay",
    "Humanize + Branding": "humanize",
    "Integrate Brand Early": "branding",
    "Optimised for Sound - Brand Mention": "brand-audio-mention",
    "Motion": "motion",
    "Optimised for Sound - Sound On": "sound-on",
    "Optimal Length": "optimal-length",
    // spelling / casing variants
    '"Humanize" + Branding': "humanize",
    "Humanize + branding": "humanize",
    "Optimized for Sound - Brand Mention": "brand-audio-mention",
    "Optimized for Sound - Sound On": "sound-on",
    "Brand Audio Mention": "brand-audio-mention",
    "Sound On": "sound-on",
    "Branding": "branding",
    "Simple Message": "simple-message",
  },
};

const byPlatformCode = new Map<string, PreflightCriterionDefinition>();
for (const entry of PREFLIGHT_CRITERIA) {
  byPlatformCode.set(`${entry.platform}:${entry.code}`, entry);
}

export function criterionForDsFeature(
  dsFeatureName: string,
  platform: PreflightPlatformCode,
): PreflightCriterionDefinition | null {
  const aliasCode = DS_FEATURE_ALIAS[platform][dsFeatureName];
  if (aliasCode) {
    return byPlatformCode.get(`${platform}:${aliasCode}`) ?? null;
  }

  const byName = PREFLIGHT_CRITERIA.find(
    (c) => c.platform === platform && c.name === dsFeatureName,
  );
  return byName ?? null;
}

const IMAGE_CONTENT_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

/** Maps confirmed upload content type to image vs video for pre-DS branches (e.g. thumbnails). */
export function assetTypeFromContentType(
  contentType: string | null | undefined,
): "IMAGE" | "VIDEO" | null {
  if (!contentType) return null;
  if (IMAGE_CONTENT_TYPES.has(contentType)) return "IMAGE";
  if (contentType.startsWith("video/")) return "VIDEO";
  return null;
}
